import { ref, type Ref } from 'vue'
import { useLocaleStore } from '@/stores'
import { toast } from '@rapoo/element/components/ui/sonner'

/** 任务类型：记录队列中等待或执行中的任务 */
interface QueuedTask {
  taskId: string
  fn: () => Promise<unknown>
  options?: {
    skipIfLoading?: boolean
    delayMs?: number
    minDurationMs?: number
    /** 是否在队列中连续相同 taskId 时跳过 */
    skipDuplicate?: boolean
  }
  resolve: (value: unknown) => void
  reject: (reason: unknown) => void
}

export function useLoading(
  initial = false,
  defaults?: {
    /** loading 状态最少持续时长（ms），避免快速结束时闪烁 */
    minDurationMs?: number
  },
) {
  const { t } = useLocaleStore()
  /** 引用计数：0=未加载, >0=加载中 */
  const loadingCount = ref(0)
  /** 对外暴露的 loading 状态：只要有任一任务在执行即为 true */
  const isLoading: Ref<boolean> = ref(initial) as unknown as Ref<boolean>

  /** 任务队列 */
  const taskQueue: QueuedTask[] = []

  /** 当前正在执行的 taskId，无任务时为 undefined */
  let currentTaskId: string | undefined

  /** 旧路径：记录当前正在执行的任务标识，用于防重入 */
  const executingFns = new Set<string>()

  function syncLoading() {
    isLoading.value = loadingCount.value > 0
  }

  /**
   * 包裹异步函数，自动管理 loading 状态。
   *
   * 兼容两种调用方式：
   * 1. withLoading(fn, options?) — 旧版引用计数（连续相同 fn 引用视为重复）
   * 2. withLoading(taskId, fn, options?) — 新版队列（相同 taskId 防重入，不同 id 排队执行）
   *
   * @param taskIdOrFn 任务标识（string）或执行函数
   * @param fnOrOptions 执行函数或配置项
   * @param options 配置项（仅当第一个参数为 string 时有效）
   */
  async function withLoading<T>(
    taskIdOrFn: string | (() => Promise<T>),
    fnOrOptions?:
      | (() => Promise<T>)
      | { skipIfLoading?: boolean; delayMs?: number; minDurationMs?: number },
    options?: { skipIfLoading?: boolean; delayMs?: number; minDurationMs?: number },
  ): Promise<T | undefined> {
    // ====== 兼容旧调用：withLoading(fn, options?) ======
    if (typeof taskIdOrFn === 'function') {
      const fn = taskIdOrFn
      const opts = fnOrOptions as
        | { skipIfLoading?: boolean; delayMs?: number; minDurationMs?: number }
        | undefined

      // 用函数名做标识（具名函数 name 唯一，匿名函数 fallback 到空字符串）
      const fnId = fn.name || fn.toString().slice(0, 80)

      // 防重入：相同的函数正在执行时跳过（嵌套调用相同函数也跳过，防止自递归）
      if (opts?.skipIfLoading !== false && executingFns.has(fnId)) {
        return undefined
      }

      executingFns.add(fnId)
      loadingCount.value++
      syncLoading()
      const startedAt = Date.now()
      const minDuration = opts?.minDurationMs ?? defaults?.minDurationMs ?? 0

      const execute = async () => {
        try {
          return await fn()
        } finally {
          executingFns.delete(fnId)
          if (minDuration > 0) {
            const remaining = minDuration - (Date.now() - startedAt)
            if (remaining > 0) {
              await new Promise((resolve) => setTimeout(resolve, remaining))
            }
          }
          loadingCount.value--
          syncLoading()
        }
      }

      return opts?.delayMs
        ? new Promise((resolve, reject) => {
            setTimeout(async () => {
              try {
                resolve(await execute())
              } catch (err) {
                reject(err)
              }
            }, opts.delayMs!)
          })
        : execute()
    }

    // ====== 新版队列调用：withLoading(taskId, fn, options?) ======
    const taskId = taskIdOrFn
    const fn = fnOrOptions as () => Promise<T>
    const opts = options

    // 防重入检查：同一个 taskId 正在执行或已在队列中时跳过
    if (currentTaskId === taskId || taskQueue.some((t) => t.taskId === taskId)) {
      console.warn(
        `Task "${taskId}" skipped: a task with the same ID is already executing or queued.`,
      )
      toast.info(t('toast.loading'))
      return undefined
    }

    return new Promise<T | undefined>((resolve, reject) => {
      const queuedTask: QueuedTask = {
        taskId,
        fn,
        options: opts,
        resolve: resolve as (value: unknown) => void,
        reject,
      }

      if (taskQueue.length === 0 && currentTaskId === undefined) {
        taskQueue.push(queuedTask)
        processQueue()
      } else {
        taskQueue.push(queuedTask)
      }
    })
  }

  /** 处理队列中的下一个任务 */
  async function processQueue() {
    if (taskQueue.length === 0) {
      currentTaskId = undefined
      return
    }

    const task = taskQueue.shift()!
    currentTaskId = task.taskId

    loadingCount.value++
    syncLoading()
    const startedAt = Date.now()
    const minDuration = task.options?.minDurationMs ?? defaults?.minDurationMs ?? 0

    try {
      const result = await task.fn()
      task.resolve(result)
    } catch (err) {
      task.reject(err)
    } finally {
      loadingCount.value--
      if (minDuration > 0) {
        const remaining = minDuration - (Date.now() - startedAt)
        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining))
        }
      }
      syncLoading()
      currentTaskId = undefined
      processQueue()
    }
  }

  return { isLoading, withLoading }
}
